"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../../Components/ThemeContext";

export default function TechStackAccordionForITSolution() {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isDark } = useTheme();

  // ✅ SEO: updated categories for Custom Software Development
  const itStack = {
    automation:     { title: "AI & Automation",            color: "green",  items: ["AI Workflow Automation", "Machine Learning APIs", "Robotic Process Automation (UiPath)", "Predictive Analytics", "Chatbots (Dialogflow, Rasa)"] },
    infrastructure: { title: "Cloud & DevOps",             color: "blue",   items: ["AWS Cloud", "Microsoft Azure", "Google Cloud Platform", "Docker & Kubernetes", "Serverless Architecture"] },
    cybersecurity:  { title: "Cybersecurity & Compliance", color: "purple", items: ["Penetration Testing", "Data Encryption Standards", "Firewall & Endpoint Protection", "Network Monitoring", "GDPR & ISO Compliance"] },
    enterprise:     { title: "Enterprise & Integration",   color: "orange", items: ["Custom Business Software", "API Integration Services", "ERP Systems (SAP, Odoo)", "CRM Tools (Salesforce, HubSpot)", "System Automation"] },
  };

  const colorClasses = {
    blue:   "bg-blue-500",
    green:  "bg-green-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    show:   { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show:   { opacity: 1, y: 0, transition: { duration: 0.3 } },
  };

  const wrapperBg = isDark ? "bg-zinc-900"   : "bg-[#1a2e1a]";
  const cardBg    = isDark ? "bg-zinc-800"   : "bg-[#0f1f0f]";
  const subText   = isDark ? "text-gray-400" : "text-gray-300";

  return (
    // ✅ SEO: <section> landmark with aria-labelledby
    <section
      aria-labelledby="custom-software-tech-stack-heading"
      className="w-full max-w-6xl mx-auto p-4 -mt-8 md:-mt-12"
    >
      {/* ✅ SEO: visually hidden H2 — keywords always in DOM regardless of accordion state */}
      <h2 id="custom-software-tech-stack-heading" className="sr-only">
        Custom Software Development Tech Stack — AWS, Azure, Docker, Kubernetes, Python, TensorFlow, Power BI
      </h2>

      {/* Accordion trigger */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="custom-software-tech-stack-content"
        className={`w-full ${wrapperBg} border-2 border-green-400 rounded-2xl p-6 flex items-center justify-between group transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,197,94,0.6)]`}
      >
        <div className="flex items-center gap-4">
          <div
            className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0 text-xl"
            aria-hidden="true"
          >
            💡
          </div>
          <div className="text-left">
            <p className="text-white text-xl md:text-2xl font-semibold">For the technically curious</p>
            <p className={`${subText} text-sm md:text-base mt-1`}>
              Expand to see our full custom software development tech stack
            </p>
          </div>
        </div>
        <div className="flex-shrink-0" aria-hidden="true">
          <svg
            className={`w-6 h-6 text-gray-400 transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* ✅ SEO: id matches aria-controls on button */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            id="custom-software-tech-stack-content"
            role="region"
            aria-labelledby="custom-software-tech-stack-heading"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
            className="mt-4 overflow-hidden"
          >
            <motion.div
              className={`${wrapperBg} rounded-2xl p-6 md:p-8`}
              variants={containerVariants}
              initial="hidden"
              animate="show"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {Object.entries(itStack).map(([key, category]) => (
                  <motion.div
                    key={key}
                    variants={itemVariants}
                    className={`space-y-4 ${cardBg} p-4 rounded-xl hover:shadow-lg hover:shadow-green-400 transition-all`}
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full flex-shrink-0 ${colorClasses[category.color]}`}
                        aria-hidden="true"
                      />
                      {/* ✅ SEO: h3 under sr-only h2 — correct heading hierarchy */}
                      <h3 className="text-white font-semibold text-lg">{category.title}</h3>
                    </div>
                    {/* ✅ SEO: <ul><li> — each tool is individually crawlable */}
                    <ul className="flex flex-wrap gap-x-2 gap-y-1" aria-label={`${category.title} tools and technologies`}>
                      {category.items.map((item, index) => (
                        <li key={index} className="text-gray-300 text-sm">
                          {item}{index < category.items.length - 1 ? "," : ""}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ✅ SEO: always-in-DOM paragraph — crawlers index all keywords even when accordion is closed */}
      <p className="sr-only">
        Our custom software development tech stack includes: AI workflow automation, machine learning APIs,
        robotic process automation, predictive analytics for AI and automation;
        AWS, Microsoft Azure, Google Cloud Platform, Docker, Kubernetes for cloud and DevOps;
        penetration testing, data encryption, GDPR and ISO compliance for cybersecurity;
        custom business software, API integration, ERP systems, CRM tools, and system automation for enterprise solutions.
      </p>
    </section>
  );
}